/* ripple AIR "Talk to camera" player.
   Lazy by design: cards ship a poster only. A <video> is created for the ACTIVE card and torn
   down when it stops being active, and nothing loads or plays until a third of the section is
   on screen - `inView` gates every play path so clips never run (or reappear) off screen.
   The lightbox keeps the two bubbles on the video, adds the review beside it on desktop, and
   carries a buy block for the product attached to that video. */
(function () {
  'use strict';
  function qsa(el, s) { return [].slice.call(el.querySelectorAll(s)); }
  function fmt(t) {
    if (!isFinite(t) || t < 0) t = 0;
    var m = Math.floor(t / 60), s = Math.floor(t % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }
  function setP(el, r) {
    if (!isFinite(r)) r = 0;
    el.style.setProperty('--p', Math.max(0, Math.min(1, r)));
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  var TICK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
  var STAR = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M12 2l3 6.9 7.5.7-5.6 5 1.6 7.4L12 18l-6.5 4 1.6-7.4-5.6-5 7.5-.7z"/></svg>';
  var XICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ' +
    'stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  function Rtc(root) {
    this.root = root;
    this.rail = root.querySelector('[data-rtc-rail]');
    this.cards = qsa(root, '[data-rtc-card]');
    this.dots = qsa(root, '[data-rtc-dot]');
    this.i = 0; this.lb = null; this.inView = false;
    if (this.cards.length) this.init();
  }

  Rtc.prototype.lbOpen = function () {
    return !!(this.lb && this.lb.classList.contains('is-open'));
  };
  Rtc.prototype.d = function (card, k) { return card.getAttribute('data-rtc-' + k) || ''; };
  Rtc.prototype.srcs = function (card) {
    try { return JSON.parse(this.d(card, 'src') || '[]'); } catch (e) { return []; }
  };

  Rtc.prototype.mount = function (card) {
    var shell = card.querySelector('[data-rtc-shell]');
    if (!shell) return null;
    var had = shell.querySelector('video');
    if (had) return had;
    var list = this.srcs(card);
    if (!list.length) return null;
    var v = document.createElement('video');
    v.muted = true; v.defaultMuted = true;
    v.playsInline = true; v.setAttribute('playsinline', '');
    v.preload = 'auto';
    var p = this.d(card, 'poster'); if (p) v.poster = p;
    list.forEach(function (s) {
      var el = document.createElement('source');
      el.src = s.src; if (s.type) el.type = s.type;
      v.appendChild(el);
    });
    shell.appendChild(v);
    return v;
  };

  Rtc.prototype.unmount = function (card) {
    var v = card.querySelector('video');
    if (v) {
      try { v.onended = null; v.pause(); while (v.firstChild) v.removeChild(v.firstChild); v.removeAttribute('src'); v.load(); } catch (e) {}
      if (v.parentNode) v.parentNode.removeChild(v);
    }
    card.classList.remove('is-playing');
  };

  Rtc.prototype.center = function (card) {
    var r = this.rail;
    if (!r || r.scrollWidth <= r.clientWidth + 1) return;
    var left = card.offsetLeft - (r.clientWidth - card.offsetWidth) / 2;
    try { r.scrollTo({ left: Math.max(0, left), behavior: 'smooth' }); }
    catch (e) { r.scrollLeft = Math.max(0, left); }
  };

  Rtc.prototype.syncDots = function () {
    var k = this.i;
    this.dots.forEach(function (d, j) {
      d.classList.toggle('is-on', j === k);
      d.setAttribute('aria-current', j === k ? 'true' : 'false');
    });
  };

  Rtc.prototype.playAt = function (i, scroll) {
    var self = this, n = this.cards.length;
    this.i = ((i % n) + n) % n;
    this.cards.forEach(function (c, k) { if (k !== self.i) self.unmount(c); });
    this.syncDots();
    /* never start anything while the section is off screen */
    if (!this.inView || this.lbOpen()) return;
    var card = this.cards[this.i];
    var v = this.mount(card);
    if (!v) return;
    card.classList.add('is-playing');
    v.onended = function () { self.playAt(self.i + 1, true); };
    var p = v.play(); if (p && p.catch) p.catch(function () {});
    if (scroll !== false) this.center(card);
  };

  Rtc.prototype.stop = function () {
    var self = this;
    this.cards.forEach(function (c) { self.unmount(c); });
  };

  /* ------------------------------ lightbox ------------------------------ */
  Rtc.prototype.buildLb = function () {
    if (this.lb) return this.lb;
    var self = this;
    var lb = document.createElement('div');
    lb.className = 'rtc__lb';
    lb.innerHTML =
      '<button class="rtc__lbnav rtc__lbnav--prev" aria-label="Previous video">&#10094;</button>' +
      '<div class="rtc__lbin">' +
        '<div class="rtc__stage">' +
          '<span class="rtc__verified" data-lb-verified></span>' +
          '<span class="rtc__use" data-lb-use></span>' +
          '<button class="rtc__x" aria-label="Close">' + XICON + '</button>' +
          '<div class="rtc__ctrl">' +
            '<span class="rtc__time">0:00</span>' +
            '<input class="rtc__seek" type="range" min="0" max="1000" value="0" aria-label="Seek">' +
            '<button class="rtc__mute" aria-label="Mute" aria-pressed="false">' +
              '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
              '<path d="M4 9v6h4l5 4V5L8 9H4z"/><path class="rtc__wave" d="M16 8.8a4.5 4.5 0 010 6.4" ' +
              'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
            '</button>' +
          '</div>' +
        '</div>' +
        '<div class="rtc__panel">' +
          '<div class="rtc__review">' +
            '<span class="rtc__stars" data-lb-stars></span>' +
            '<p class="rtc__rquote" data-lb-quote></p>' +
            '<div class="rtc__rauthor"><span class="rtc__av" data-lb-av></span>' +
            '<span data-lb-name></span></div>' +
          '</div>' +
          '<div class="rtc__shop" data-lb-shop hidden>' +
            '<img class="rtc__shopimg" data-lb-shopimg alt="">' +
            '<span class="rtc__shopmeta"><span class="rtc__shopt" data-lb-shopt></span>' +
            '<span class="rtc__shopp" data-lb-shopp></span></span>' +
            '<button class="rtc__add" data-lb-add type="button">Add to cart</button>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<button class="rtc__lbnav rtc__lbnav--next" aria-label="Next video">&#10095;</button>';
    document.body.appendChild(lb);

    var q = function (s) { return lb.querySelector(s); };
    var stage = q('.rtc__stage'), seek = q('.rtc__seek'), time = q('.rtc__time');
    var wave = q('.rtc__wave'), mute = q('.rtc__mute');

    lb.addEventListener('click', function (e) { if (e.target === lb) self.closeLb(); });
    q('.rtc__x').addEventListener('click', function () { self.closeLb(); });
    q('.rtc__lbnav--prev').addEventListener('click', function () { self.openLb(self.i - 1); });
    q('.rtc__lbnav--next').addEventListener('click', function () { self.openLb(self.i + 1); });

    seek.addEventListener('input', function () {
      var v = self.lbVideo;
      setP(seek, seek.value / 1000);
      if (v && isFinite(v.duration)) v.currentTime = (seek.value / 1000) * v.duration;
    });
    mute.addEventListener('click', function () {
      var v = self.lbVideo; if (!v) return;
      v.muted = !v.muted;
      if (!v.muted && !v.volume) v.volume = 1;
    });
    q('[data-lb-add]').addEventListener('click', function () { self.add(this); });

    var x0 = null;
    stage.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0; x0 = null;
      if (Math.abs(dx) > 45) self.openLb(self.i + (dx < 0 ? 1 : -1));
    }, { passive: true });

    document.addEventListener('keydown', function (e) {
      if (!lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') self.closeLb();
      else if (e.key === 'ArrowRight') self.openLb(self.i + 1);
      else if (e.key === 'ArrowLeft') self.openLb(self.i - 1);
    });

    this.lb = lb;
    this.els = {
      stage: stage, seek: seek, time: time, wave: wave, mute: mute,
      verified: q('[data-lb-verified]'), use: q('[data-lb-use]'), stars: q('[data-lb-stars]'),
      quote: q('[data-lb-quote]'), av: q('[data-lb-av]'), name: q('[data-lb-name]'),
      shop: q('[data-lb-shop]'), shopimg: q('[data-lb-shopimg]'), shopt: q('[data-lb-shopt]'),
      shopp: q('[data-lb-shopp]'), add: q('[data-lb-add]')
    };
    return lb;
  };

  Rtc.prototype.fill = function (card) {
    var e = this.els, self = this;
    function d(k) { return self.d(card, k); }
    var vlabel = d('verified');
    e.verified.innerHTML = vlabel ? TICK + esc(vlabel) : '';
    e.verified.hidden = !vlabel;
    e.use.textContent = d('use');
    e.use.hidden = !d('use');
    var stars = parseInt(d('stars'), 10) || 0, out = '';
    for (var i = 0; i < stars; i++) out += STAR;
    e.stars.innerHTML = out;
    e.quote.textContent = d('quote');
    e.av.textContent = d('av');
    e.name.textContent = d('name');

    var pid = d('pid');
    e.shop.hidden = !pid;
    if (pid) {
      e.add.setAttribute('data-variant', pid);
      e.shopt.textContent = d('ptitle');
      /* The price and the struck compare-at live in the BUTTON, matching the Starter Kit
         section. Only the saving sits outside it, so no figure is repeated. */
      e.shopp.innerHTML = d('psave')
        ? '<span class="rtc__save">Save ' + esc(d('psave')) + '</span>'
        : '';
      var btn = '<span>Add to cart</span><i class="rtc__adiv" aria-hidden="true"></i>';
      if (d('pcompare')) btn += '<span class="rtc__awas">' + esc(d('pcompare')) + '</span>';
      btn += '<span>' + esc(d('pprice')) + '</span>';
      e.add.innerHTML = btn;
      if (d('pimg')) { e.shopimg.src = d('pimg'); e.shopimg.hidden = false; }
      else { e.shopimg.hidden = true; }
    }
  };

  Rtc.prototype.add = function (btn) {
    var id = btn.getAttribute('data-variant');
    if (!id || btn.disabled) return;
    var html = btn.innerHTML;
    btn.disabled = true; btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    }).then(function () {
      btn.textContent = 'Added';
      setTimeout(function () { btn.innerHTML = html; btn.disabled = false; }, 1400);
      openCart();
    }).catch(function () {
      btn.innerHTML = html; btn.disabled = false;
      window.location.href = '/cart';
    });
  };

  function openCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    try {
      if (window.Rebuy && window.Rebuy.SmartCart && window.Rebuy.SmartCart.show) {
        window.Rebuy.SmartCart.show(); return;
      }
    } catch (e) {}
    var link = document.querySelector('a.cart-link, [data-cart-drawer-toggle], .js-drawer-open-cart');
    if (link) link.click();
  }

  Rtc.prototype.openLb = function (i) {
    var self = this, n = this.cards.length;
    i = ((i % n) + n) % n;
    var lb = this.buildLb(), e = this.els;
    this.stop();
    this.i = i;
    this.syncDots();
    var card = this.cards[i];
    this.fill(card);

    if (this.lbVideo && this.lbVideo.parentNode) this.lbVideo.parentNode.removeChild(this.lbVideo);
    var v = document.createElement('video');
    v.playsInline = true; v.setAttribute('playsinline', '');
    v.preload = 'auto'; v.controls = false;
    var p = this.d(card, 'poster'); if (p) v.poster = p;
    this.srcs(card).forEach(function (s) {
      var el = document.createElement('source');
      el.src = s.src; if (s.type) el.type = s.type;
      v.appendChild(el);
    });
    e.stage.insertBefore(v, e.stage.firstChild);
    this.lbVideo = v;

    /* opening is a user gesture, so sound is allowed */
    v.muted = false; v.volume = 1;
    e.seek.value = 0; setP(e.seek, 0);
    e.wave.style.opacity = 1;
    e.mute.setAttribute('aria-pressed', 'false');
    e.time.textContent = '0:00';

    v.addEventListener('loadedmetadata', function () { e.time.textContent = '0:00 / ' + fmt(v.duration); });
    v.addEventListener('timeupdate', function () {
      if (!isFinite(v.duration) || !v.duration) return;
      var r = v.currentTime / v.duration;
      e.seek.value = Math.round(r * 1000); setP(e.seek, r);
      e.time.textContent = fmt(v.currentTime) + ' / ' + fmt(v.duration);
    });
    v.addEventListener('volumechange', function () {
      e.wave.style.opacity = v.muted ? 0 : 1;
      e.mute.setAttribute('aria-pressed', v.muted ? 'true' : 'false');
      e.mute.setAttribute('aria-label', v.muted ? 'Unmute' : 'Mute');
    });
    v.addEventListener('ended', function () { self.openLb(self.i + 1); });

    lb.classList.add('is-open');
    document.body.classList.add('rtc-locked');
    var pr = v.play();
    if (pr && pr.catch) pr.catch(function () { v.muted = true; v.play(); });
  };

  Rtc.prototype.closeLb = function () {
    if (!this.lb) return;
    this.lb.classList.remove('is-open');
    document.body.classList.remove('rtc-locked');
    var v = this.lbVideo;
    if (v) {
      try { v.pause(); while (v.firstChild) v.removeChild(v.firstChild); v.load(); } catch (e) {}
      if (v.parentNode) v.parentNode.removeChild(v);
      this.lbVideo = null;
    }
    if (this.inView) this.playAt(this.i, true); else this.stop();
  };

  /* ------------------------------ wiring ------------------------------ */
  Rtc.prototype.init = function () {
    var self = this;
    this.cards.forEach(function (c, i) {
      var hit = c.querySelector('[data-rtc-shell]') || c;
      hit.addEventListener('click', function () { self.openLb(i); });
    });
    this.dots.forEach(function (d, j) {
      d.addEventListener('click', function () { self.playAt(j, true); });
    });
    qsa(this.root, '[data-rtc-prev]').forEach(function (b) {
      b.addEventListener('click', function () { self.playAt(self.i - 1, true); });
    });
    qsa(this.root, '[data-rtc-next]').forEach(function (b) {
      b.addEventListener('click', function () { self.playAt(self.i + 1, true); });
    });

    if (!('IntersectionObserver' in window)) { this.inView = true; this.playAt(0, false); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        self.inView = e.isIntersecting;
        if (e.isIntersecting) {
          if (!self.lbOpen()) self.playAt(self.i, false);
        } else if (!self.lbOpen()) {
          self.stop();
        }
      });
    }, { rootMargin: '0px', threshold: 0.33 });
    io.observe(this.root);
  };

  function boot() { qsa(document, '[data-rtc]').forEach(function (r) { if (!r.__rtc) r.__rtc = new Rtc(r); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
